//
// https://github.com/tlhunter/node-autotile
//
import { BITMASK, E, N, NE, NW, S, SE, SW, W } from "./autotile_bitmask"
import { BLOCK_WALL_ITEMS, COMPUTABLE_WALL_AUTOTILE_INDICES } from "./autotile_library"

export { autotileIndexBitmask } from "./autotile_bitmask"

/** Whether cell (x, y) joins its neighbour (nx, ny) -- both present -- in an autotile. */
export type AutotileJoins = (x: number, y: number, nx: number, ny: number) => boolean

function autotileLookup(map, x_boundary, y_boundary, x, y, joins?: AutotileJoins) {
    const at = (nx: number, ny: number) => !!map[ny][nx] && (!joins || joins(x, y, nx, ny))
    let sum = 0
    let n = false
    let e = false
    let s = false
    let w = false

    if (!map[y][x]) return 0

    if (y > 0 && at(x, y - 1)) {
        n = true
        sum += N
    }
    if (x > 0 && at(x - 1, y)) {
        w = true
        sum += W
    }
    if (x < x_boundary && at(x + 1, y)) {
        e = true
        sum += E
    }
    if (y < y_boundary && at(x, y + 1)) {
        s = true
        sum += S
    }

    if (n && w && y > 0 && x > 0 && at(x - 1, y - 1)) sum += NW
    if (n && e && y > 0 && x < x_boundary && at(x + 1, y - 1)) sum += NE
    if (s && w && y < y_boundary && x > 0 && at(x - 1, y + 1)) sum += SW
    if (s && e && x < x_boundary && y < y_boundary && at(x + 1, y + 1)) sum += SE

    return BITMASK[sum]
}

// Autotile indices (the BITMASK values) a wall can be drawn at: the ones EVERY wall material in
// content/game/maps/autotiles.json has a rule for (kind "wall"; see wallDrawableIndices). A wall cell whose
// computed index falls outside this set has NO computable tile -- e.g. a 2x2 block, whose four cells
// compute to indices 34/26/7/4 -- so that placement is invalid. Derived, so a rule added to or
// removed from the data changes what may be placed with it, also by a live publication (the set is
// kept, and rebound, by autotile_library.ts).
export { COMPUTABLE_WALL_AUTOTILE_INDICES }

// Given a value from an autotile index grid, returns whether that cell is renderable as a wall.
// autotileLookup returns 0 for an empty cell (no wall -> fine), 1..47 for a wall (renderable only
// if every wall material has a rule for it), or undefined when the neighbor-sum has no BITMASK entry
// (only happens for wall cells -> uncomputable).
export const wallAutoTileCellComputable = (idx: number | undefined): boolean => {
    if (idx === 0) return true // no wall here
    if (idx === undefined) return false // wall with an unmapped neighbor configuration
    return COMPUTABLE_WALL_AUTOTILE_INDICES.has(idx)
}

/**
 * As wallAutoTileCellComputable, for a cell whose material is known: a solid block (block_walls.ts) draws every index,
 * so any shape of blocks is placeable -- they join only their own kind, so they shape no other wall.
 */
export const wallCellComputable = (idx: number | undefined, itemType?: string): boolean =>
    itemType && BLOCK_WALL_ITEMS.has(itemType) ? idx !== undefined : wallAutoTileCellComputable(idx)

/** Each cell's autotile index; a neighbour counts only where `joins` (default: any present neighbour) allows. */
export const autotile = (map, joins?: AutotileJoins) => {
    const tiles = []
    const height = map.length
    const width = map[0].length
    const x_boundary = width - 1
    const y_boundary = height - 1

    for (let y = 0; y < height; y++) {
        tiles[y] = []
        for (let x = 0; x < width; x++) {
            tiles[y][x] = autotileLookup(map, x_boundary, y_boundary, x, y, joins)
        }
    }

    return tiles
}

/**
 * The autotile indices of a grid of wall cells of any materials. Every material joins every other (a wooden wall
 * joins a stone one) except one that joins only its own kind (`joins: "own"` on its definition, a railing): its cells
 * are tiled among themselves, and every other wall as if they were not there -- a railing beside a wall ends at a
 * post of its own and leaves the wall's shape alone. `own(cell)` names the kind a cell joins only, if any. `indices`:
 * each cell's index; `gridAt(x, y)`: the index grid the cell's rule reads its neighbours in.
 */
export const wallAutotile = <C>(map: (C | undefined | null)[][], own: (cell: C) => string | undefined): { indices: number[][]; gridAt: (x: number, y: number) => number[][] } => {
    const kinds = new Set<string>()
    map.forEach(row => row.forEach(cell => {
        const kind = cell ? own(cell) : undefined
        if (kind) kinds.add(kind)
    }))
    if (!kinds.size) {
        const indices = autotile(map)
        return { indices, gridAt: () => indices }
    }
    const only = (kind: string | undefined) => map.map(row => row.map(cell => (cell && own(cell) === kind ? cell : undefined)))
    const common = autotile(only(undefined))
    const grids = new Map([...kinds].map(kind => [kind, autotile(only(kind))]))
    const gridAt = (x: number, y: number) => {
        const cell = map[y]?.[x]
        const kind = cell ? own(cell) : undefined
        return (kind && grids.get(kind)) || common
    }
    return { indices: map.map((row, y) => row.map((_, x) => gridAt(x, y)[y][x])), gridAt }
}
